import { useParams as useRouterParams } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { ContentBreadcrumbs } from "../../components/ContentBreadcrumbs";
import { Box, Button, Typography } from "@mui/material";
import { useGetContentModelFieldsQuery, useGetContentModelQuery, useGetContentNavItemsQuery, useGetLangsQuery } from "../../../../../../shell/services/instance";
import { ItemListEmpty } from "./ItemListEmpty";
import { ItemListActions } from "./ItemListActions";
import { useEffect, useMemo, useRef, useState, useContext, useCallback } from "react";
import { SearchRounded, RestartAltRounded } from "@mui/icons-material";
import noSearchResults from "../../../../../../../public/images/noSearchResults.svg";
import { ItemListFilters } from "./ItemListFilters";
import { useParams } from "../../../../../../shell/hooks/useParams";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../../shell/store/types";
import { useStagedChanges } from "./StagedChangesContext";
import { UpdateListActions } from "./UpdateListActions";
import { getDateFilterFnByValues } from "../../../../../../shell/components/Filters/DateFilter/getDateFilter";
import { ItemListTable } from "./ItemListTable";
import { useSelectedItems } from "./SelectedItemsContext";
import { useGetUsersQuery } from "../../../../../../shell/services/accounts";
import { ContentItem, ContentItemWithDirtyAndPublishing, ContentModelFieldDataType } from "../../../../../../shell/services/types";
import { fetchAllModelPublishings, fetchItems } from "../../../../../../shell/store/content";
import { TableSortContext } from "./TableSortProvider";
import { fetchFields } from "../../../../../../shell/store/fields";
import { debounce } from "lodash";
import { SkeletonContentHeader, SkeletonItemListFilters } from "./Loader";
import { NotFound } from "../../../../../../shell/components/NotFound";
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC"
});
const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric"
});
const formatDateTime = (source: string) => {
  if (!source) return "";
  const dateObj = new Date(source);
  if (isNaN(dateObj.getTime())) return "";
  const date = dateFormatter.format(dateObj);
  const time = timeFormatter.format(dateObj);
  return `${date} ${time}`;
};
const formatDate = (source: string) => {
  if (!source) return "";
  const dateObj = new Date(source);
  if (isNaN(dateObj.getTime())) return "";
  return dateFormatter.format(dateObj);
};
export const ItemList = () => {
  const {
    t
  } = useTranslation();
  const {
    modelZUID
  } = useRouterParams<{
    modelZUID: string;
  }>();
  const [params, setParams] = useParams();
  const dispatch = useDispatch();
  const activeLanguageCode = params.get("lang");
  const {
    data: model,
    isFetching: isModelFetching,
    error: modelError
  } = useGetContentModelQuery(modelZUID);
  const {
    data: fields,
    isFetching: isFieldsFetching
  } = useGetContentModelFieldsQuery({
    modelZUID
  });
  const {
    data: languages,
    isLoading: isLangsLoading
  } = useGetLangsQuery({});
  const activeLangId = languages?.find(lang => lang.code === activeLanguageCode)?.ID || 1;
  const allItems = useSelector((state: AppState) => state.content);
  const items = useMemo(() => {
    return Object.values(allItems).filter((item: ContentItem) => item.meta && item.meta.contentModelZUID === modelZUID && item.meta.langID === activeLangId);
  }, [allItems, modelZUID, activeLangId]);
  const allFields = useSelector((state: AppState) => state.fields);
  const user = useSelector((state: AppState) => state.user);
  const {
    data: users,
    isFetching: isUsersFetching
  } = useGetUsersQuery();
  const [processedItems, setProcessedItems] = useState([]);
  const [isModelItemsFetching, setIsModelItemsFetching] = useState(true);
  const [sortModel] = useContext(TableSortContext);
  const {
    stagedChanges
  } = useStagedChanges();
  const [selectedItems] = useSelectedItems();
  const searchRef = useRef<HTMLInputElement>(null);
  const search = params.get("search");
  // const sort = params.get("sort");
  const statusFilter = params.get("statusFilter");
  const dateFilter = useMemo(() => {
    return {
      preset: params.get("datePreset") || "",
      from: params.get("from") || "",
      to: params.get("to") || ""
    };
  }, [params]);
  const userFilter = params.get("user");
  const isFetchingData = isModelFetching || isFieldsFetching || isLangsLoading || isUsersFetching;
  const fieldMap = useMemo(() => {
    if (!fields?.length) return new Map<string, any>();
    return new Map(fields.filter(field => !field.deletedAt).map(field => [field.name, field]));
  }, [fields]);
  const resolveFieldRelationshipTitle = useCallback((fieldName: string, fieldDataType: ContentModelFieldDataType, relatedContentItemZUID: string) => {
    if (!fields?.length || !allFields || !allItems || !fieldName || !fieldDataType || !relatedContentItemZUID) {
      return;
    }
    if (fieldDataType === "internal_link") {
      return allItems?.[relatedContentItemZUID]?.web?.metaTitle || relatedContentItemZUID;
    }
    const fieldData = fieldMap.get(fieldName);
    const relatedFieldData = allFields?.[fieldData?.relatedFieldZUID];
    return allItems?.[relatedContentItemZUID]?.data?.[relatedFieldData?.name] ?? relatedContentItemZUID;
  }, [allItems, allFields, fieldMap]);
  useEffect(() => {
    dispatch(fetchFields(modelZUID));
  }, [modelZUID]);
  useEffect(() => {
    if (activeLanguageCode && modelZUID && !isFetchingData) {
      setIsModelItemsFetching(true);
      Promise.all([dispatch(fetchItems(modelZUID, {
        limit: 1000,
        page: 1,
        lang: activeLanguageCode
      })), dispatch(fetchAllModelPublishings({
        modelZUID
      }))]).finally(() => {
        setIsModelItemsFetching(false);
      });
    }
  }, [modelZUID, activeLanguageCode, isFetchingData]);
  useEffect(() => {
    // if languages and no language param, set the user selected lang or first language as the active language
    if (languages && !activeLanguageCode) {
      setParams(user?.selected_lang || languages[0].code, "lang");
    }
  }, [languages, activeLanguageCode]);
  const computeProcessedItems = useCallback(() => {
    if (!items || isFieldsFetching || isUsersFetching) return [];
    const userMap = new Map(users?.map(user => [user.ZUID, user]));
    return items.map((item: ContentItemWithDirtyAndPublishing) => {
      const {
        meta,
        data,
        web,
        publishing
      } = item;
      const metaUser = userMap.get(meta.createdByUserZUID);
      const webUser = userMap.get(web.createdByUserZUID);
      const publishedByUser = userMap.get(publishing?.publishedByUserZUID);
      const scheduledByUser = userMap.get(item.scheduling?.publishedByUserZUID);
      const clonedItem = {
        ...item,
        id: meta.ZUID,
        meta: {
          ...meta,
          createdAt: meta.createdAt ? formatDateTime(meta.createdAt) : null,
          createdByUserName: metaUser?.firstName ? `${metaUser.firstName} ${metaUser.lastName}` : null,
          createdByUserEmail: metaUser?.email || "",
          publishedByUserName: publishedByUser?.firstName ? `${publishedByUser.firstName} ${publishedByUser.lastName}` : null,
          scheduledByUserName: scheduledByUser?.firstName ? `${scheduledByUser.firstName} ${scheduledByUser.lastName}` : null
        },
        web: {
          ...web,
          updatedAt: web.updatedAt ? formatDateTime(web.updatedAt) : null,
          createdByUserName: webUser?.firstName ? `${webUser.firstName} ${webUser.lastName}` : null
        },
        data: {
          ...data
        },
        publishing: {
          ...publishing,
          publishAt: publishing?.publishAt ? formatDateTime(publishing.publishAt) : null
        },
        fieldData: {}
      };
      Object.keys(data).forEach(key => {
        const fieldData = fieldMap.get(key);
        const fieldType = fieldData?.datatype;
        const value = data[key] as string;
        switch (fieldType) {
          case "images":
            clonedItem.data[key] = value?.split(",")?.[0];
            break;
          case "internal_link":
          case "one_to_one":
            clonedItem.data[key] = resolveFieldRelationshipTitle(key, fieldType, value);
            break;
          case "one_to_many":
            clonedItem.data[key] = value?.split(",")?.map(id => resolveFieldRelationshipTitle(key, fieldType, id))?.join(",");
            break;
          case "date":
            clonedItem.data[key] = formatDate(value);
            break;
          case "datetime":
            clonedItem.data[key] = formatDateTime(value);
            break;
          case "dropdown":
          case "yes_no":
            // @ts-ignore
            clonedItem.fieldData[key] = fieldData;
            break;
          case "integration":
            clonedItem.data[key] = !value ? "" : JSON.stringify(value);
            break;
          default:
            break;
        }
      });
      return clonedItem;
    });
  }, [items, fields, users, isFieldsFetching, isUsersFetching, fieldMap]);

  /*
    We debounce the processed items compute since certain fields can call for items to be fetched if they are not in memory
    and we don't want to trigger a heavy computation on every item fetched in parallel. This reduces initial load time.
  */
  const debouncedCompute = useMemo(() => {
    return debounce(() => {
      setProcessedItems(computeProcessedItems());
    }, 300);
  }, [computeProcessedItems]);
  useEffect(() => {
    debouncedCompute();
    return () => debouncedCompute.cancel();
  }, [debouncedCompute, items, allItems, fieldMap, users, isFieldsFetching, isUsersFetching]);
  const sortedAndFilteredItems = useMemo(() => {
    const sort = sortModel?.[0]?.field;
    const sortOrder = sortModel?.[0]?.sort;
    let clonedItems = [...processedItems];
    clonedItems?.sort((a: any, b: any) => {
      if (!sort || sort === "lastSaved") {
        const dateA = new Date(a.web.createdAt).getTime();
        const dateB = new Date(b.web.createdAt).getTime();
        if (!a.web.createdAt) {
          return -1;
        } else if (!b.web.createdAt) {
          return 1;
        } else {
          return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        }
      } else if (sort === "lastPublished") {
        // Handle undefined publishAt by setting a default far-future date for sorting purposes

        let dateA = a?.scheduling?.publishAt || a?.publishing?.publishAt;
        dateA = dateA ? new Date(dateA).getTime() : Number.NEGATIVE_INFINITY;
        let dateB = b?.scheduling?.publishAt || b?.publishing?.publishAt;
        dateB = dateB ? new Date(dateB).getTime() : Number.NEGATIVE_INFINITY;
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      } else if (sort === "createdOn") {
        if (sortOrder === "asc") {
          return new Date(a.meta.createdAt).getTime() - new Date(b.meta.createdAt).getTime();
        }
        return new Date(b.meta.createdAt).getTime() - new Date(a.meta.createdAt).getTime();
      } else if (sort === "version") {
        const aIsPublished = a?.publishing?.publishAt;
        const bIsPublished = b?.publishing?.publishAt;
        const aIsScheduled = a?.scheduling?.publishAt;
        const bIsScheduled = b?.scheduling?.publishAt;

        // Check if meta.version exists
        const aHasVersion = a?.meta?.version != null;
        const bHasVersion = b?.meta?.version != null;

        // Place items without meta.version at the bottom
        if (!aHasVersion && bHasVersion) {
          return 1;
        } else if (aHasVersion && !bHasVersion) {
          return -1;
        }

        // Items with only publish date
        if (aIsPublished && !aIsScheduled && bIsPublished && !bIsScheduled) {
          if (sortOrder === "asc") {
            return new Date(aIsPublished).getTime() - new Date(bIsPublished).getTime();
          }
          return new Date(bIsPublished).getTime() - new Date(aIsPublished).getTime(); // Both have only published date, sort by publish date descending
        } else if (aIsPublished && !aIsScheduled) {
          return -1; // A has only published date, B does not
        } else if (bIsPublished && !bIsScheduled) {
          return 1; // B has only published date, A does not
        }

        // Items with scheduled date (and also publish date)
        if (aIsScheduled && bIsScheduled) {
          if (sortOrder === "asc") {
            return new Date(bIsScheduled).getTime() - new Date(aIsScheduled).getTime();
          }
          return new Date(aIsScheduled).getTime() - new Date(bIsScheduled).getTime(); // Both are scheduled, sort by scheduled date ascending
        } else if (aIsScheduled) {
          return -1; // A is scheduled, B is not
        } else if (bIsScheduled) {
          return 1; // B is scheduled, A is not
        }

        // Items with neither publish nor schedule dates
        if (aIsPublished && bIsPublished) {
          if (sortOrder === "asc") {
            return new Date(aIsPublished).getTime() - new Date(bIsPublished).getTime();
          }
          return new Date(bIsPublished).getTime() - new Date(aIsPublished).getTime(); // Both are published, sort by publish date descending
        } else if (aIsPublished) {
          return -1; // A is published, B is not
        } else if (bIsPublished) {
          return 1; // B is published, A is not
        }
        return 0; // Neither are published or scheduled
      } else if (sort === "createdBy") {
        const userA = a?.meta?.createdByUserName;
        const userB = b?.meta?.createdByUserName;
        if (!userA) {
          return 1;
        } else if (!userB) {
          return -1;
        } else {
          return sortOrder === "asc" ? userB.localeCompare(userA) : userA.localeCompare(userB);
        }
      } else if (sort === "zuid") {
        return sortOrder === "asc" ? b.meta?.ZUID?.localeCompare(a.meta?.ZUID) : a.meta?.ZUID?.localeCompare(b.meta?.ZUID);
      } else if (fields?.find(field => field.name === sort)) {
        const dataType = fields?.find(field => field.name === sort)?.datatype;
        if (typeof a.data[sort] === "number" || dataType === "sort" || dataType === "number") {
          if (a.data[sort] == null) return 1;
          if (b.data[sort] == null) return -1;
          if (dataType === "sort") {
            return sortOrder === "asc" ? a.data[sort] - b.data[sort] : b.data[sort] - a.data[sort];
          }
          return sortOrder === "asc" ? a.data[sort] - b.data[sort] : b.data[sort] - a.data[sort];
        }
        if (dataType === "date" || dataType === "datetime") {
          if (!a.data[sort]) {
            return 1;
          } else if (!b.data[sort]) {
            return -1;
          } else {
            return sortOrder === "asc" ? new Date(a.data[sort]).getTime() - new Date(b.data[sort]).getTime() : new Date(b.data[sort]).getTime() - new Date(a.data[sort]).getTime();
          }
        }
        if (dataType === "yes_no") {
          if (!a.data[sort]) {
            return 1;
          } else if (!b.data[sort]) {
            return -1;
          } else {
            return sortOrder === "asc" ? a - b : b - a;
          }
        }
        const aValue = dataType === "images" ? a.data[sort]?.filename : a.data[sort] || "";
        const bValue = dataType === "images" ? b.data[sort]?.filename : b.data[sort] || "";
        return sortOrder === "asc" ? String(bValue ?? "").trim().localeCompare(String(aValue ?? "").trim()) : String(aValue ?? "").trim().localeCompare(String(bValue ?? "").trim());
      } else {
        return sortOrder === "asc" ? new Date(a.meta.updatedAt).getTime() - new Date(b.meta.updatedAt).getTime() : new Date(b.meta.updatedAt).getTime() - new Date(a.meta.updatedAt).getTime();
      }
    });
    if (search) {
      clonedItems = clonedItems?.filter(item => {
        return Object.values(item.data).some((value: any) => {
          if (!value) return false;
          if (value?.filename || value?.title) {
            return value?.filename?.toLowerCase()?.includes(search.toLowerCase()) || value?.title?.toLowerCase()?.includes(search.toLowerCase());
          }
          return value.toString().toLowerCase().includes(search.toLowerCase());
        }) || item?.meta?.createdAt?.toLowerCase().includes(search.toLowerCase()) || item?.web?.updatedAt?.toLowerCase().includes(search.toLowerCase()) || item?.publishing?.publishAt?.toLowerCase().includes(search.toLowerCase()) || item?.scheduling?.publishAt?.toLowerCase().includes(search.toLowerCase()) || item?.meta?.ZUID?.toLowerCase().includes(search.toLowerCase()) || item?.meta?.createdByUserName?.toLowerCase().includes(search.toLowerCase());
      });
    }
    if (statusFilter) {
      clonedItems = clonedItems?.filter(item => {
        if (statusFilter === "published") {
          return item.publishing?.publishAt && !item.scheduling?.publishAt;
        } else if (statusFilter === "scheduled") {
          return item.scheduling?.publishAt;
        } else if (statusFilter === "notPublished") {
          return !item.publishing?.publishAt && !item?.scheduling?.publishAt;
        }
      });
    }
    const dateFilterFn = getDateFilterFnByValues(dateFilter);
    if (dateFilterFn) {
      clonedItems = clonedItems.filter(item => {
        if (item?.meta?.updatedAt) {
          return dateFilterFn(item?.meta?.updatedAt);
        }
        return false;
      });
    }
    if (userFilter) {
      clonedItems = clonedItems.filter(item => item?.meta?.createdByUserZUID === userFilter);
    }
    return clonedItems;
  }, [processedItems, search, sortModel, statusFilter, dateFilter, userFilter]);
  if (modelError && "status" in modelError) {
    let message = "";
    switch (modelError.status) {
      case 404:
        message = t("content.itemListModelNotFound", {
          modelZUID
        });
        break;
      case 400:
        message = t("content.itemListModelInvalid", {
          modelZUID
        });
        break;
      default:
        break;
    }
    return <NotFound message={message} />;
  }
  return <Box sx={{
    color: "text.primary",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "*": {
      boxSizing: "border-box"
    }
  }} data-sentry-element="Box" data-sentry-component="ItemList" data-sentry-source-file="index.tsx">
      <Box sx={{
      px: 4,
      pt: 4,
      pb: 2,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "start",
      gap: 4,
      minHeight: "106px"
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        {stagedChanges && Object.keys(stagedChanges)?.length || selectedItems?.length ? <UpdateListActions items={items as ContentItem[]} /> : <>
            {isUsersFetching || isLangsLoading ? <SkeletonContentHeader /> : <>
                <Box flex={1}>
                  <ContentBreadcrumbs />
                  <Typography variant="h3" mt={0.25} fontWeight={700} sx={{
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
              wordWrap: "break-word",
              hyphens: "auto",
              overflow: "hidden"
            }}>
                    {model?.label}
                  </Typography>
                </Box>
                <ItemListActions ref={searchRef} />
              </>}
          </>}
      </Box>
      <Box height="100%" bgcolor="grey.50" px={4} sx={{
      display: "flex",
      flexDirection: "column"
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        {!items?.length && !isModelItemsFetching ? <ItemListEmpty /> : <>
            {isFieldsFetching || isUsersFetching ? <SkeletonItemListFilters /> : <ItemListFilters />}
            <ItemListTable key={modelZUID} loading={isFieldsFetching || isUsersFetching || isModelItemsFetching} rows={sortedAndFilteredItems} fields={isFieldsFetching ? [] : fields} noRowsOverlay={() => {
          if (search && !isModelItemsFetching) {
            return <Box bgcolor="common.white" flex={1} display="flex" alignItems="center" height="100%">
                      <Box data-cy="NoResults" textAlign="center" sx={{
                maxWidth: 387,
                mx: "auto"
              }}>
                        <img src={noSearchResults} alt={t("content.itemListNoSearchResultsAlt")} />
                        <Typography pt={4} pb={1} variant="h4" fontWeight={600}>
                          <Trans i18nKey="content.itemListSearchNoResultsTitleRich" values={{
                    search
                  }} components={{
                    strong: <strong />
                  }} />
                        </Typography>
                        <Typography variant="body2" pb={3} color="text.secondary">
                          {t("content.itemListSearchNoResultsBody")}
                        </Typography>
                        <Button onClick={() => searchRef.current?.focus()} variant="contained" startIcon={<SearchRounded />}>
                          {t("common.searchAgain")}
                        </Button>
                      </Box>
                    </Box>;
          }
          if (!isModelItemsFetching && !search && (statusFilter || dateFilter?.preset || dateFilter?.from || dateFilter?.to || userFilter)) {
            return <Box bgcolor="common.white" flex={1} display="flex" alignItems="center" height="100%">
                      <Box data-cy="NoResults" textAlign="center" sx={{
                maxWidth: 387,
                mx: "auto"
              }}>
                        <img src={noSearchResults} alt={t("content.itemListNoSearchResultsAlt")} />
                        <Typography pt={4} pb={1} variant="h4" fontWeight={600}>
                          {t("content.itemListFilterNoResultsTitle")}
                        </Typography>
                        <Typography variant="body2" pb={3} color="text.secondary">
                          {t("content.itemListFilterNoResultsBody")}
                        </Typography>
                        <Button onClick={() => {
                  setParams(null, "statusFilter");
                }} variant="contained" startIcon={<RestartAltRounded />}>
                          {t("content.itemListResetFilters")}
                        </Button>
                      </Box>
                    </Box>;
          }
          return <></>;
        }} />
          </>}
      </Box>
    </Box>;
};